import { useEffect, useMemo, useState } from "react";
import Select, { type MultiValue } from "react-select";
import { Edit, Plus, Trash2, X } from "lucide-react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { ConfirmationModal } from "../../../../components/confirmation-modal";
import { Table } from "../../../../components/table";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type { Tenant } from "../../../../types/tenant";
import type { TenantNotification } from "../../../../types/notification";

type TenantOption = { value: string; label: string };
type FormState = {
  id?: string;
  tenantIds: string[];
  title: string;
  body: string;
};
const emptyForm: FormState = { tenantIds: [], title: "", body: "" };

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

export default function NotificationsPage() {
  const { success, error, warn } = useToast();
  const [notifications, setNotifications] = useState<TenantNotification[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [toDelete, setToDelete] = useState<TenantNotification | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const options = useMemo<TenantOption[]>(
    () =>
      tenants
        .filter((tenant): tenant is Tenant & { id: string } =>
          Boolean(tenant.id),
        )
        .map((tenant) => ({ value: tenant.id, label: tenant.appName })),
    [tenants],
  );
  const tenantNames = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options],
  );

  async function load() {
    try {
      setLoading(true);
      const [notificationResponse, tenantResponse] = await Promise.all([
        api.get<TenantNotification[]>("/notifications"),
        api.get<{ data: Tenant[] }>("/tenant", {
          params: { page: 1, limit: 1000 },
        }),
      ]);
      setNotifications(notificationResponse.data ?? []);
      setTenants(tenantResponse.data.data ?? []);
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form.title.trim()) return warn("Informe o título.");
    if (!form.tenantIds.length) return warn("Selecione ao menos um tenant.");
    try {
      setSubmitting(true);
      const payload = {
        tenantIds: form.tenantIds,
        title: form.title.trim(),
        body: form.body.trim() || undefined,
      };
      if (form.id) await api.patch(`/notifications/${form.id}`, payload);
      else await api.post("/notifications", payload);
      success(
        form.id
          ? "Notificação atualizada com sucesso."
          : "Notificação cadastrada com sucesso.",
      );
      setForm(emptyForm);
      await load();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function remove() {
    if (!toDelete) return;
    try {
      setDeleting(true);
      await api.delete(`/notifications/${toDelete.id}`);
      success("Notificação removida com sucesso.");
      setToDelete(null);
      if (form.id === toDelete.id) setForm(emptyForm);
      await load();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  const columns = [
    {
      key: "title",
      label: "Título",
      render: (item: TenantNotification) => (
        <div>
          <p className="font-medium text-slate-900">{item.title}</p>
          {item.body && (
            <p className="mt-1 max-w-md truncate text-xs text-slate-500">
              {item.body}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "tenants",
      label: "Tenants",
      render: (item: TenantNotification) =>
        item.tenantIds.map((id) => tenantNames.get(id) ?? id).join(", "),
    },
    {
      key: "date",
      label: "Data e hora",
      render: (item: TenantNotification) => formatDateTime(item.createdAt),
    },
    {
      key: "actions",
      label: "Ações",
      render: (item: TenantNotification) => (
        <div className="flex justify-center gap-3">
          <button
            type="button"
            className="btn-link-primary"
            title="Editar"
            onClick={() =>
              setForm({
                id: item.id,
                tenantIds: item.tenantIds,
                title: item.title,
                body: item.body ?? "",
              })
            }
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="btn-link-danger"
            title="Excluir"
            onClick={() => setToDelete(item)}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="app-page">
      <BackButton />
      <h1 className="app-page-title">Notificações</h1>
      <p className="app-page-subtitle mb-6">
        Envie avisos gerais para um ou mais tenants.
      </p>
      <form onSubmit={submit} className="app-card mb-6 p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="app-section-title">
            {form.id ? "Editar notificação" : "Nova notificação"}
          </h2>
          {form.id && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setForm(emptyForm)}
            >
              <X className="h-4 w-4" /> Cancelar
            </button>
          )}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Título
            <input
              className="form-input"
              required
              maxLength={200}
              value={form.title}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
            />
          </label>
          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Tenants
            <Select<TenantOption, true>
              isMulti
              options={options}
              value={options.filter((option) =>
                form.tenantIds.includes(option.value),
              )}
              onChange={(items: MultiValue<TenantOption>) =>
                setForm((current) => ({
                  ...current,
                  tenantIds: items.map((item) => item.value),
                }))
              }
              placeholder="Selecione os tenants"
              noOptionsMessage={() => "Nenhum tenant encontrado"}
            />
          </label>
          <label className="flex flex-col gap-2 text-sm text-slate-700 md:col-span-2">
            Mensagem (opcional)
            <textarea
              className="form-textarea min-h-28"
              value={form.body}
              onChange={(event) =>
                setForm((current) => ({ ...current, body: event.target.value }))
              }
            />
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <LoadingButton
            type="submit"
            className="btn btn-neutral"
            isLoading={submitting}
            loadingLabel="Salvando"
          >
            <Plus className="h-4 w-4" />
            {form.id ? "Salvar alterações" : "Cadastrar notificação"}
          </LoadingButton>
        </div>
      </form>
      {loading ? (
        <div className="app-card p-8 text-center text-slate-500">
          Carregando...
        </div>
      ) : (
        <Table data={notifications} columns={columns} />
      )}
      <ConfirmationModal
        isOpen={Boolean(toDelete)}
        title="Excluir notificação"
        description={`Deseja excluir a notificação “${toDelete?.title ?? ""}”?`}
        confirmLabel="Excluir"
        onConfirm={() => void remove()}
        onCancel={() => setToDelete(null)}
        isLoading={deleting}
      />
    </div>
  );
}
